import { useState } from "react";
import dayjs from "dayjs";
import { Plus, Edit, Trash2, X, Save, Loader2 } from "lucide-react";
import { 
  useAllNoticesQuery, 
  useCreateNoticeMutation, 
  useUpdateNoticeMutation, 
  useDeleteNoticeMutation 
} from "../../../hooks/useNoticeQuery";
import { useAppDialog } from '../../../hooks/useAppDialog';
import { GetNoticeBadge } from "../../../components/common/GetNoticeBadge"
import { type NoticeType, NoticeConst } from "../../../constants/NoticeConst";

export const NoticeManagement = () => {
  const { data: notices = [], isLoading } = useAllNoticesQuery();
  const createMutation = useCreateNoticeMutation();
  const updateMutation = useUpdateNoticeMutation();
  const deleteMutation = useDeleteNoticeMutation();

  // 共通ダイアログ用フック
  const { showConfirm } = useAppDialog();
  const pageTitle = "お知らせメンテ機能";

  // モーダル制御とフォーム状態
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    publishedAt: dayjs().format("YYYY-MM-DDTHH:mm"),
    noticeType: NoticeConst.Normal as NoticeType,
    title: "",
    content: ""
  });

  // 新規作成ボタン
  const handleAddNew = () => {
    setEditingId(null);
    setFormData({
      publishedAt: dayjs().format("YYYY-MM-DDTHH:mm"), // デフォルトは現在時刻
      noticeType: NoticeConst.Normal,
      title: "",
      content: ""
    });
    setIsModalOpen(true);
  };

  // 編集ボタン
  const handleEdit = (notice: any) => {
    setEditingId(notice.id);
    setFormData({
      publishedAt: dayjs(notice.publishedAt).format("YYYY-MM-DDTHH:mm"),
      noticeType: (notice.noticeType ?? NoticeConst.Normal) as NoticeType,
      title: notice.title,
      content: notice.content
    });
    setIsModalOpen(true);
  };

  // 保存処理
  const handleSave = async () => {
    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, request: formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
      setIsModalOpen(false);
    } catch (e) {
      alert("保存に失敗しました。");
    }
  };

  // 削除処理
  const handleDelete = async (id: number) => {
    // if (window.confirm("本当に削除してもよろしいですか？")) {
    //   try {
    //     await deleteMutation.mutateAsync(id);
    //   } catch (e) {
    //     alert("削除に失敗しました。");
    //   }
    // }
    showConfirm({
      title: pageTitle,
      message: `削除してもよろしいですか？`,
      onConfirm: () => {
        deleteMutation.mutateAsync(id);
      }
    });    
  };

  if (isLoading) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>;

return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">お知らせ管理</h2>
        <button 
          onClick={handleAddNew}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2 text-sm font-bold transition-colors"
        >
          <Plus size={18} /> 新規登録
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-100 border-b-2 border-slate-200">
              <th className="p-3 text-sm font-bold text-slate-600 whitespace-nowrap">公開日時 (設定)</th>
              <th className="p-3 text-sm font-bold text-slate-600 whitespace-nowrap">状態</th>
              <th className="p-3 text-sm font-bold text-slate-600 whitespace-nowrap">カテゴリ</th> {/* ★追加 */}
              <th className="p-3 text-sm font-bold text-slate-600 w-full">タイトル</th>
              <th className="p-3 text-sm font-bold text-slate-600 text-center w-32">操作</th>
            </tr>
          </thead>
          <tbody>
            {notices.map((notice) => {
              const isPublished = dayjs(notice.publishedAt).isBefore(dayjs());
              return (
                <tr key={notice.id} className="border-b hover:bg-slate-50">
                  <td className="p-3 text-sm text-slate-700 whitespace-nowrap">
                    {dayjs(notice.publishedAt).format("YYYY/MM/DD HH:mm")}
                  </td>
                  <td className="p-3 text-sm whitespace-nowrap">
                    {isPublished ? (
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-bold">公開中</span>
                    ) : (
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-bold">予約中</span>
                    )}
                  </td>
                  <td className="p-3">
                    {GetNoticeBadge(notice.noticeType as NoticeType)}
                  </td>
                  <td className="p-3 text-sm font-medium text-slate-800">{notice.title}</td>
                  <td className="p-3">
                    <div className="flex justify-center gap-2">
                      <button onClick={() => handleEdit(notice)} className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition-colors">
                        <Edit size={18} />
                      </button>
                      <button onClick={() => handleDelete(notice.id)} className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {notices.length === 0 && (
              <tr>
                <td colSpan={5} className="p-6 text-center text-slate-500">登録されているお知らせはありません。</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 登録・編集モーダル */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="font-bold text-lg">{editingId ? "お知らせを編集" : "新規お知らせ登録"}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-800">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-bold text-gray-700 mb-1">掲載開始日時</label>
                  <input 
                    type="datetime-local" 
                    value={formData.publishedAt}
                    onChange={(e) => setFormData({...formData, publishedAt: e.target.value})}
                    className="w-full sm:w-48 p-2 h-[42px] border border-slate-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors appearance-none min-w-0 safari-date-fix"
                  />
                </div>
                {/* ★ カテゴリ選択プルダウンを追加 */}
                <div className="w-1/3">
                  <label className="block text-sm font-bold text-gray-700 mb-1">カテゴリ</label>
                  <select
                    value={formData.noticeType}
                    onChange={(e) => setFormData({...formData, noticeType: Number(e.target.value) as NoticeType})}
                    className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                  >
                    <option value={0}>通常</option>
                    <option value={1}>重要</option>
                    <option value={2}>システム</option>
                    <option value={3}>取引</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">タイトル</label>
                <input 
                  type="text" 
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="お知らせのタイトル"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">本文</label>
                <textarea 
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  className="w-full border p-2 rounded h-40 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                  placeholder="お知らせの内容を改行を含めて入力してください"
                />
              </div>
            </div>

            <div className="p-4 border-t flex justify-end gap-3 bg-gray-50 rounded-b-lg">
              <button 
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 border bg-white rounded hover:bg-gray-100 font-medium"
              >
                キャンセル
              </button>
              <button 
                onClick={handleSave}
                disabled={createMutation.isPending || updateMutation.isPending || !formData.title || !formData.content}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {(createMutation.isPending || updateMutation.isPending) ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoticeManagement;